/**
 * Lazy access to `mvdan-sh`, the JS build of the parser behind `shfmt`. It is
 * the only bash parser we found that gets heredocs right (`bash-parser` reparses
 * the body as further commands; `shell-quote` is a tokenizer that drops newlines
 * and silently expands `$VAR` to the empty string), but it is a ~260 KB gzipped
 * GopherJS bundle. That is far too much to put on the chat's critical path for a
 * presentational nicety, so it is loaded on demand: the first tool row that
 * could be a file op kicks off the import, and rows re-render once it lands.
 */
import type sh from 'mvdan-sh';
import { useEffect, useSyncExternalStore } from 'react';

type Syntax = typeof sh.syntax;

export type ShellSyntax = Syntax;

let syntax: Syntax | null = null;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

/** The parser once it has loaded, or `null` while it is still on the wire. */
export function getShellSyntax(): Syntax | null {
  return syntax;
}

/**
 * Start loading the parser. Safe to call repeatedly; the import happens once and
 * a failure is not retried (the feature simply stays off).
 */
export function loadShellParser(): Promise<void> {
  loading ??= import('mvdan-sh')
    .then((mod) => {
      syntax = (mod.default ?? mod).syntax;
      for (const listener of listeners) listener();
    })
    .catch(() => {
      // A parser we can't load just means no Read/Write affordances.
    });
  return loading;
}

/**
 * `mvdan-sh` exposes Go's `RedirOperator` enum as opaque numbers with no runtime
 * names, so the only honest way to get one is to ask the parser: parse a probe
 * command that uses the operator and read the value back off its redirection.
 * `redirOperator(syntax, "x <<'E'\nE")` is the heredoc operator. A probe that
 * somehow didn't parse yields `-1`, an operator nothing can equal.
 */
const redirOpCache = new WeakMap<object, Map<string, number>>();

export function redirOperator(syntax: Syntax, probe: string): number {
  let cache = redirOpCache.get(syntax);
  if (!cache) {
    cache = new Map();
    redirOpCache.set(syntax, cache);
  }
  const cached = cache.get(probe);
  if (cached !== undefined) return cached;
  const op = (syntax.NewParser().Parse(probe, 'probe.sh').Stmts[0]?.Redirs[0]?.Op ??
    -1) as unknown as number;
  cache.set(probe, op);
  return op;
}

function subscribe(listener: () => void) {
  // Once the parser is in hand it never changes again, so a long transcript
  // doesn't keep a listener per tool row.
  if (syntax) return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The parser, loading it on first use. `enabled` is false for rows that can
 * never be a file op, so an all-`Read`/`Edit` conversation never pays for the
 * download.
 */
export function useShellParser(enabled: boolean): Syntax | null {
  const loaded = useSyncExternalStore(
    subscribe,
    () => syntax,
    () => null,
  );
  useEffect(() => {
    // In an effect, not in render: a render React throws away shouldn't pull a
    // 260 KB chunk over the wire.
    if (enabled) void loadShellParser();
  }, [enabled]);
  return enabled ? loaded : null;
}
